import type { PhoneNumberData } from '@cae/cobalt-react/components/phone-number-input';

import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useNavigationBlock } from '@shared/hooks/use-navigation-block/useNavigationBlock';
import * as React from 'react';

import type { EditContactFormData } from '../../schemas/editContactPanel.schema';
import type {
  ContactFormState,
  UseEditContactPanelOptions,
  UseEditContactPanelReturn,
} from './useEditContactPanel.types';

import { editContactSchema } from '../../schemas/editContactPanel.schema';
import {
  convertToClientProfileFormat,
  getPhoneRegion,
} from '../../utils/format-phone-number/formatPhoneNumber.utils';
import { useProfile } from '../use-profile/useProfile';
import { useUpdateProfileContact } from '../use-update-profile-contact/useUpdateProfileContact';
import {
  buildInitialContactFormState,
  normalizeContactPhoneValue,
  resolvePhoneCountry,
} from './useEditContactPanel.utils';

export function useEditContactPanel({
  onClose,
}: UseEditContactPanelOptions): UseEditContactPanelReturn {
  const { profile, isProfileLoading } = useProfile();
  const { mutate, isPending, isError, isSuccess } = useUpdateProfileContact();
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const [isExitModalOpen, setIsExitModalOpen] = React.useState<boolean>(false);
  const [isPhoneValid, setIsPhoneValid] = React.useState<boolean>(true);

  const [formState, setFormState] = React.useState<ContactFormState>(() =>
    buildInitialContactFormState(profile)
  );

  const [phoneInputCountry, setPhoneInputCountry] = React.useState<string>(
    () => resolvePhoneCountry(getPhoneRegion(formState.mobileNumber))
  );

  const initialized = React.useRef(false);
  const initialFormStateRef = React.useRef<ContactFormState>(formState);
  const sanitizedStateRef = React.useRef<EditContactFormData | null>(null);
  const isPageRefreshingRef = React.useRef(false);
  const isDiscardingRef = React.useRef(false);

  React.useEffect(() => {
    const handleBeforeUnload = (): void => {
      isPageRefreshingRef.current = true;
    };
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  React.useEffect(() => {
    if (isSuccess) {
      emitFlagMessage('success', 'Changes saved successfully!', 5000);
      onClose();
    }
  }, [isSuccess, onClose]);

  React.useEffect(() => {
    if (profile && !initialized.current) {
      initialized.current = true;
      const initial = buildInitialContactFormState(profile);
      initialFormStateRef.current = initial;
      setFormState(initial);
      if (initial.mobileNumber) {
        setPhoneInputCountry(resolvePhoneCountry(getPhoneRegion(initial.mobileNumber)));
      }
    }
  }, [profile]);

  const handlePhoneNumberChange = React.useCallback(
    (data: PhoneNumberData): void => {
      setFormState(prev => ({ ...prev, mobileNumber: data.number ?? '' }));
      setPhoneInputCountry(resolvePhoneCountry(data.country?.toLowerCase()));
      setIsPhoneValid(!data.number || data.isValid);
    },
    []
  );

  const validate = React.useCallback((): boolean => {
    const result = editContactSchema.safeParse(formState);
    if (result.success) {
      sanitizedStateRef.current = result.data;
      return true;
    }
    return false;
  }, [formState]);

  const isDirty =
    !isSuccess &&
    normalizeContactPhoneValue(formState.mobileNumber) !==
      normalizeContactPhoneValue(initialFormStateRef.current.mobileNumber);

  const isDirtyRef = React.useRef(isDirty);
  isDirtyRef.current = isDirty;

  useNavigationBlock(
    React.useCallback(
      (): boolean => isDirtyRef.current && !isDiscardingRef.current,
      []
    )
  );

  const handleSubmit = React.useCallback((): void => {
    if (
      !isDirtyRef.current ||
      !isPhoneValid ||
      !validate() ||
      !sanitizedStateRef.current
    )
      return;

    const sanitized = sanitizedStateRef.current;
    const e164Mobile = sanitized.mobileNumber.trim() || null;
    mutate({
      mobileNumber: e164Mobile
        ? convertToClientProfileFormat(e164Mobile)
        : null,
    });
  }, [validate, isPhoneValid, mutate]);

  const handleCloseAttempt = React.useCallback((): void => {
    if (isPageRefreshingRef.current) return;
    if (isDirty) {
      setIsExitModalOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleExit = React.useCallback((): void => {
    isDiscardingRef.current = true;
    setIsExitModalOpen(false);
    onClose();
  }, [onClose]);

  const handleKeepEditing = React.useCallback((): void => {
    setIsExitModalOpen(false);
  }, []);

  const isProfileReady = !!profile;

  return {
    formState,
    isMobile,
    isExitModalOpen,
    isDirty,
    isPending,
    isError,
    isProfileLoading,
    isProfileReady,
    isPhoneValid,
    phoneInputValue: formState.mobileNumber,
    phoneInputCountry,
    handlePhoneNumberChange,
    handleSubmit,
    handleCloseAttempt,
    handleExit,
    handleKeepEditing,
  };
}
